import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Currency } from 'src/app/shared/enums/price/currencies.enum';
import { EventType } from 'src/app/shared/enums/event/event-types.enum';
import { collapseAnimation } from 'src/app/shared/animations/collapse.animation';
import { finalize, Subscription, take } from 'rxjs';
import { UtilsService } from 'src/app/shared/services/utils.service';
import { AdminPostRegistrationWithEventResultType, UsersService } from 'src/app/shared/services/entities/users/users.service';
import { IEventPostData } from 'src/app/shared/models/event/event-post-data.model';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { NewEventModalComponent } from 'src/app/shared/modals/new-event-modal/new-event-modal.component';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { AuthModalComponent } from 'src/app/shared/modals/auth-modal/auth-modal.component';
import { AuthComponentState } from 'src/app/shared/components/auth/auth.component';
import { LpCreateEventService } from './lp-create-event.service';
import { EventTypeRadioInputComponent } from 'src/app/shared/components/event-type-radio-input/event-type-radio-input.component';
import { MixpanelService } from 'src/app/shared/services/mixpanel.service';
import { TranslateService } from '@ngx-translate/core';
import { Language } from 'src/app/shared/enums/utils/languages.enum';
import { emailValidator } from 'src/app/shared/utils/session-group-form-utils/validators';
import { EventFormat } from 'src/app/shared/models/event/event.model';

export const LP_CREATE_EVENT_ID = 'LP_CREATE_EVENT';

interface IForm {
  title: FormControl<string>;
  format: FormControl<EventFormat>;
  email: FormControl<string>;
  currencies: FormControl<Currency[]>;
};

@Component({
  selector: 'app-lp-create-event',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputTextModule, ButtonModule, EventTypeRadioInputComponent],
  templateUrl: './lp-create-event.component.html',
  styleUrls: ['./lp-create-event.component.scss'],
  animations: [ collapseAnimation ]
})
export class LpCreateEventComponent implements OnInit, OnDestroy, AfterViewInit {
  EventFormat = EventFormat;
  LP_CREATE_EVENT_ID = LP_CREATE_EVENT_ID;

  form = new FormGroup<IForm>({
    title: new FormControl('', { nonNullable: true , validators: [ Validators.required ]}),
    format: new FormControl(EventFormat.EVENT, { nonNullable: true, validators: [ Validators.required] }),
    currencies: new FormControl(Object.values(Currency), { nonNullable: true, validators: [ Validators.required ]}),
    email: new FormControl('', { nonNullable: true, validators: [ Validators.required, emailValidator ]})
  });

  showTitleContinue = true;
  typeFormCollapsed = true;
  emailFormCollapsed = true;

  sending = false;
  subs: Subscription[] = [];
  titlePlaceholderHints = [
    { label: 'Swing pro začátečníky', index: 0, active: true },
    { label: 'Podzimní akrobacie pro děti', index: 0, active: false },
    { label: 'Konference Změna ve škole', index: 0, active: false },
  ];

  @ViewChild('titleInput') titleInput: ElementRef | undefined;
  animationInterval: any;
  stoppedAnimationCurrentPlaceholder = '';
  retryAnimationTimeout: any;

  constructor(
    private utilsService: UtilsService,
    private usersService: UsersService,
    private mixpanelService: MixpanelService,
    private authService: AuthService,
    private modalService: ModalService,
    private navService: NavigationService,
    private lpCreateEventService: LpCreateEventService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {

    this.subs.push(
      // show email part of form when type selected
      this.typeFC?.valueChanges.subscribe(type => {
        if (type) this.emailFormCollapsed = false;
      }) ?? Subscription.EMPTY,

      this.lpCreateEventService.focusTitleInput.subscribe(() => {
        this.titleInput?.nativeElement.focus();
      }),
    )
  }

  ngAfterViewInit(): void {
    this.animateTitleInput();
  }

  onSubmit() {
    this.mixpanelService.track('lp.signup.createevent.click');

    this.utilsService.markFormGroupDirty(this.form);
    if (this.form.invalid) {
      console.error('invalid');
      return;
    }

    const formVals = this.form.getRawValue();
    const data: IEventPostData = {
      title: formVals.title,
      currencies: formVals.currencies,
      email: formVals.email,
      format: formVals.format,
      userPreferredLanguage: this.translate.currentLang as Language,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    this.sending = true;
    this.usersService.postRegistrationWithEvent(data).pipe(
      take(1),
      finalize(() => this.sending = false)
    ).subscribe({
      next: async (res) => {
        switch (res.resultType) {
          case AdminPostRegistrationWithEventResultType.NEW_USER_CREATED:
            await this.authService.signInWithToken(res.signInToken!, false);
            if (res.event) this.navService.goToAdminEvent({
              eventUuid: res.event.uuid
            });
            break;
          case AdminPostRegistrationWithEventResultType.SIGN_IN:
            const signInRes = this.modalService.openAuthModal({
              c: AuthModalComponent,
              title: "admin.authmodal.login.title",
              initEmail: this.emailFC?.value,
              setRedirectAfterLoad: false,
              state: AuthComponentState.SIGN_IN
            });
            signInRes.pipe(take(1)).subscribe((res) => {
              if (res) this.navigateUserToAdmin();
            });
            break;
          default:
            this.utilsService.logError('Should not happen! 9iuowpeup;oewqijdoi');
            break;
        }
      }
    })
  }

  animateTitleInput() {
    if (!this.titleInput) {
      this.utilsService.logError('Unable to animate title input! 1o28uew8pourqp9w');
      return;
    }

    let keyInputTime = 80;
    this.animationInterval = setInterval(() => {
      if (!this.titleInput) return;

      const el = this.titleInput.nativeElement;
      const activePlaceholderHintIndex = this.titlePlaceholderHints.findIndex(x => x.active)!;
      const activePlaceholderHint = this.titlePlaceholderHints[activePlaceholderHintIndex];
      if (activePlaceholderHint.index === 0) el.placeholder = '';
      const currentPlaceholder = el.placeholder;

      el.placeholder = currentPlaceholder + activePlaceholderHint.label[activePlaceholderHint.index];

      activePlaceholderHint.index++;
      if (activePlaceholderHint.index >= activePlaceholderHint.label.length) {
        activePlaceholderHint.index = 0;
        // activate next
        activePlaceholderHint.active = false;
        if (activePlaceholderHintIndex + 1 > this.titlePlaceholderHints.length - 1) {
          this.titlePlaceholderHints[0].active = true;
        } else {
          this.titlePlaceholderHints[activePlaceholderHintIndex + 1].active = true;
        }
        // wait some time and run again
        clearInterval(this.animationInterval);
        this.retryAnimationTimeout = setTimeout(() => {
          this.animateTitleInput();
        },3000)
      }


    },keyInputTime)
  }

  onTitleInputFocus() {
    if (!this.titleInput) return;
    this.pauseAnimation();
    this.titleInput.nativeElement.placeholder = 'Napište název akce';

  }
  onTitleInputBlur() {
    this.resumeAnimation();
  }

  pauseAnimation() {
    clearInterval(this.animationInterval);
    clearTimeout(this.retryAnimationTimeout);
    this.stoppedAnimationCurrentPlaceholder = this.titleInput?.nativeElement.placeholder ?? '';
  }
  resumeAnimation() {
    if (!this.titleInput) return;
    this.titleInput.nativeElement.placeholder = this.stoppedAnimationCurrentPlaceholder;
    this.animateTitleInput();
  }

  onTitleContinue() {
    this.mixpanelService.track('lp.signup.start.click');

    this.utilsService.markControlDirty(this.titleFC);
    if (this.titleFC.invalid) {
      console.error('invalid');
      return;
    }

    if (this.authService.isAuthenticated()) {
      this.navigateUserToAdmin();
      return;
    }

    this.showTitleContinue = false;
    this.typeFormCollapsed = false;
  }

  private navigateUserToAdmin() {
    this.navService.goToAdminEvents().then(() => {
      this.modalService.openNewEventModal({
        c: NewEventModalComponent,
        prefillTitle: this.titleFC.value
      });
    });
  }


  get emailFC() {
    return this.form.get('email');
  }
  get titleFC() {
    return this.form.get('title') as FormControl<string>;
  }
  get typeFC() {
    return this.form.get('type');
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }
}
